// analytics/tabs/StationsTrendsTab.jsx - Updated with unified filters

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
// import inferenceService from '../../../js/Inspection/services/inferenceService.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { TrendChart } from '../components/charts/TrendChart.jsx';
import StationComparisonChart from '../components/charts/StationComparisonChart.jsx';

// Use the unified filter hook and components
import { useTabFilters } from '../hooks/useTabFilters.js';
import { UnifiedFilterPanel } from '../components/filters/UnifiedFilterComponents.jsx'

// Import the reusable inspection components
import {
    InspectionTable,
    InspectionDetailsModal as ReusableInspectionDetailsModal,
    ImageModal
} from '../components/InspectionComponents.jsx';

/**********************************************************************
 * Station Details Modal Component - Enhanced with pagination
 *********************************************************************/
const StationDetailsModal = ({ station, isOpen, onClose, ...props }) => {
    if (!isOpen || !station) return null;

    const {
        trendData, isTrendLoading, recentInspections, isInspectionsLoading,
        onViewInspectionDetails, onImageClick, onSetGroundTruth, isGroundTruthLoading,
        addNotification, inspectionPagination, onInspectionPageChange
    } = props;

    const stats = station.statistics || {};
    const defectRate = stats.total_inspections > 0 ? ((stats.defect_count / stats.total_inspections) * 100) : 0;
    const defectRateColor = defectRate > 10 ? 'danger' : defectRate > 5 ? 'warning' : 'success';
    const successRate = 100 - defectRate;

    const lastUpdated = station.last_config_update?.timestamp 
        ? new Date(station.last_config_update.timestamp).toLocaleString() 
        : 'N/A';
    const description = station.notes || 'No description provided.';

    return (
        <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-server me-2"></i>
                            Station Details: {station.station_name || station.station_id}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {/* Station Information Card */}
                        <div className="card mb-4">
                            <div className="card-header"><h6>Station Information</h6></div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <p><strong>Station Name:</strong> {station.station_name || 'N/A'}</p>
                                        <p><strong>Station ID:</strong> <code>{station.station_id}</code></p>
                                        <p><strong>Status:</strong> 
                                            <span className={`badge bg-${station.status === 'online' ? 'success' : 'secondary'} ms-1`}>
                                                {station.status}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p><strong>Location:</strong> {station.location || 'N/A'}</p>
                                        <p><strong>Last Config Update:</strong> {lastUpdated}</p>
                                        <p><strong>Notes:</strong> {description}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Metrics Cards */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card text-white bg-primary h-100">
                                    <div className="card-body text-center">
                                        <i className="fas fa-microscope fa-2x mb-2"></i>
                                        <h4>{stats.total_inspections?.toLocaleString() || 0}</h4>
                                        <small>Total Inspections</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className={`card text-white bg-${defectRateColor} h-100`}>
                                    <div className="card-body text-center">
                                        <i className="fas fa-percentage fa-2x mb-2"></i>
                                        <h4>{defectRate.toFixed(1)}%</h4>
                                        <small>Defect Rate</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card text-white bg-success h-100">
                                    <div className="card-body text-center">
                                        <i className="fas fa-check-circle fa-2x mb-2"></i>
                                        <h4>{successRate.toFixed(1)}%</h4>
                                        <small>Success Rate</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card text-white bg-warning h-100">
                                    <div className="card-body text-center">
                                        <i className="fas fa-exclamation-triangle fa-2x mb-2"></i>
                                        <h4>{stats.defect_count || 0}</h4>
                                        <small>Defects Found</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trend Chart */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    <i className="fas fa-chart-line text-primary me-2"></i>
                                    Quality Trend Over Time
                                </h6>
                            </div>
                            <div className="card-body">
                                {isTrendLoading ? (
                                    <LoadingSpinner message="Loading trend data..." />
                                ) : trendData && trendData.length > 0 ? (
                                    <div style={{ height: '300px' }}>
                                        <TrendChart 
                                            chartId={`station-trend-${station.station_id}`} 
                                            data={trendData}
                                            title={`Quality Trend for ${station.station_name || station.station_id}`}
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center text-muted p-4">
                                        <i className="fas fa-chart-line fa-3x mb-3"></i>
                                        <h6>No Trend Data Available</h6>
                                        <p className="small">Trend data requires multiple inspections over time.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recent Inspections Table */}
                        <div>
                            {isInspectionsLoading && recentInspections.length === 0 ? (
                                <LoadingSpinner message="Loading inspections..." />
                            ) : (
                                <InspectionTable 
                                    inspections={recentInspections} 
                                    onViewDetails={onViewInspectionDetails} 
                                    onImageClick={onImageClick} 
                                    onSetGroundTruth={onSetGroundTruth} 
                                    isLoading={isGroundTruthLoading || isInspectionsLoading} 
                                    showExportButtons={false} 
                                    title={`Inspections for ${station.station_name || station.station_id}`}
                                    addNotification={addNotification} 
                                />
                            )}
                        </div>

                        {/* Pagination for Inspections */}
                        {inspectionPagination.totalPages > 1 && (
                            <div className="card mt-3 border-0 bg-light">
                                <div className="card-body py-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">
                                            Showing {((inspectionPagination.page - 1) * inspectionPagination.limit) + 1} to {
                                                Math.min(inspectionPagination.page * inspectionPagination.limit, inspectionPagination.total)
                                            } of {inspectionPagination.total.toLocaleString()} inspections
                                        </div>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => onInspectionPageChange(1)}
                                                        disabled={inspectionPagination.page === 1}
                                                    >
                                                        «
                                                    </button>
                                                </li>
                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => onInspectionPageChange(inspectionPagination.page - 1)}
                                                        disabled={inspectionPagination.page === 1}
                                                    >
                                                        ‹
                                                    </button>
                                                </li>
                                                <li className="page-item active">
                                                    <span className="page-link">
                                                        {inspectionPagination.page} of {inspectionPagination.totalPages}
                                                    </span>
                                                </li>
                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => onInspectionPageChange(inspectionPagination.page + 1)}
                                                        disabled={inspectionPagination.page >= inspectionPagination.totalPages}
                                                    >
                                                        ›
                                                    </button>
                                                </li>
                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => onInspectionPageChange(inspectionPagination.totalPages)}
                                                        disabled={inspectionPagination.page >= inspectionPagination.totalPages}
                                                    >
                                                        »
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**********************************************************************
 * Station Card Component - Improved design
 *********************************************************************/
const StationCard = ({ station, onViewDetails }) => {
    const stats = station.statistics || {};
    const defectRate = stats.total_inspections > 0 ? 
        ((stats.defect_count / stats.total_inspections) * 100).toFixed(1) : '0.0';
    
    const rateColor = parseFloat(defectRate) > 10 ? 'danger' : parseFloat(defectRate) > 5 ? 'warning' : 'success';
    const statusColor = station.status === 'online' ? 'success' : 'secondary';

    return (
        <div className="col-md-6 col-lg-4 mb-3">
            <div className="card h-100 shadow-sm">
                <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h5 className="card-title mb-1">{station.station_name || station.station_id}</h5>
                            <p className="text-muted small mb-0">
                                <i className="fas fa-tag me-1"></i>
                                ID: <code>{station.station_id}</code>
                            </p>
                        </div>
                        <span className={`badge bg-${statusColor}`}>
                            <i className={`fas ${station.status === 'online' ? 'fa-broadcast-tower' : 'fa-exclamation-triangle'} me-1`}></i>
                            {station.status}
                        </span>
                    </div>

                    {/* Performance Metrics */}
                    <div className="row text-center mb-3">
                        <div className="col-4 border-end">
                            <h6 className={`mb-0 text-${rateColor}`}>{defectRate}%</h6>
                            <small className="text-muted">Defect Rate</small>
                        </div>
                        <div className="col-4 border-end">
                            <h6 className="mb-0 text-primary">{stats.total_inspections?.toLocaleString() || 0}</h6>
                            <small className="text-muted">Total</small>
                        </div>
                        <div className="col-4">
                            <h6 className="mb-0 text-warning">{stats.defect_count || 0}</h6>
                            <small className="text-muted">Defects</small>
                        </div>
                    </div>

                    {/* Additional info */}
                    <p className="text-muted small mb-2">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {station.location || 'No location set'}
                    </p>
                    <div className="mt-auto">
                        <button 
                            className="btn btn-primary btn-sm w-100" 
                            onClick={() => onViewDetails(station)}
                        >
                            View Details & Trend
                        </button>
                    </div>
                </div>
            </div>
        </div>  
    );
};

/**********************************************************************
 * Main Tab Component - Updated with unified filters
 *********************************************************************/
export const StationsTrendsTab = () => {
    const { state, setError, addNotification } = useAppContext();
    const { itemsPerPage, includeManualImages } = state.preferences;
    
    // Use unified filters with station-specific defaults
    const {
        filters,
        handleFilterChange,
        resetFilters,
        getConfiguredFilters,
        hasInitialized
    } = useTabFilters(
        {
            sortBy: 'defect_rate' // Station-specific default
        }
    );

    // State for filter dropdown options
    const [availableOptions, setAvailableOptions] = useState({ projects: [], lotIds: [] });
    
    // State for stations list
    const [stations, setStations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // State for Station Details Modal
    const [showStationDetailsModal, setShowStationDetailsModal] = useState(false);
    const [detailsStation, setDetailsStation] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [isTrendLoading, setIsTrendLoading] = useState(false);
    const [recentInspections, setRecentInspections] = useState([]);
    const [isInspectionsLoading, setIsInspectionsLoading] = useState(false);
    const [inspectionPagination, setInspectionPagination] = useState({ 
        page: 1, 
        limit: 10, 
        total: 0, 
        totalPages: 1 
    });

    // State for managing the nested InspectionComponents
    const [modelList, setModelList] = useState([]);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [showInspectionDetails, setShowInspectionDetails] = useState(false);
    const [largeImage, setLargeImage] = useState(null);
    const [isGroundTruthLoading, setIsGroundTruthLoading] = useState(false);

    // Load initial data (projects and models)
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [projectsRes, modelsRes] = await Promise.all([
                    api.getProjects().catch(err => {
                        console.error('[StationsTrendsTab] Failed to load projects:', err);
                        return [];
                    }),
                    api.getAvailableModels(null).catch(err => {
                        console.error('[StationsTrendsTab] Failed to load models:', err);
                        return [];
                    })
                ]);
                
                setAvailableOptions(prev => ({
                    ...prev,
                    projects: Array.isArray(projectsRes) ? projectsRes.map(p => p.name) : []
                }));
                setModelList(modelsRes || []);
            } catch (error) {
                addNotification?.({ 
                    type: 'warning', 
                    message: 'Some page data could not be loaded.' 
                });
            }
        };
        
        loadInitialData();
    }, [addNotification]);

    // Load lot IDs when project filter changes
    useEffect(() => {
        if (!filters.project) {
            setAvailableOptions(prev => ({ ...prev, lotIds: [] }));
            return;
        }
        
        api.getLotIdentifiers(filters.project)
            .then(lotIds => {
                setAvailableOptions(prev => ({ ...prev, lotIds: lotIds || [] }));
            })
            .catch(err => {
                console.error('[StationsTrendsTab] Failed to load lot IDs:', err);
                setAvailableOptions(prev => ({ ...prev, lotIds: [] }));
            });
    }, [filters.project]);

    // Load stations data when filters change
    const loadStations = useCallback(async () => {
        if (!hasInitialized) return;

        try {
            setIsLoading(true);
            setError(null);
            
            const configuredFilters = getConfiguredFilters();
            const data = await api.getStations(configuredFilters);
            setStations(data || []);
        } catch (error) {
            setError(error);
            if (addNotification) {
                addNotification({
                    type: 'danger',
                    message: `Failed to load stations: ${error.message}`
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [filters, setError, addNotification, getConfiguredFilters, hasInitialized]);

    // Reload stations when filters change
    useEffect(() => {
        loadStations();
    }, [loadStations]);

    // --- Event Handlers ---

    const handleRefresh = useCallback(() => {
        loadStations();
    }, [loadStations]);

    // --- Modal Handlers ---

    const loadStationInspections = useCallback(async (stationId, page = 1, limit = 10) => {
        setIsInspectionsLoading(true);
        try {
            const configuredFilters = getConfiguredFilters();
            const detailFilters = {
                station: stationId,
                ...configuredFilters,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            };

            const inspectionsResult = await api.getInspections(detailFilters, page, limit);
            
            setRecentInspections(inspectionsResult?.data || []);
            setInspectionPagination({
                page: inspectionsResult?.page || page,
                limit: inspectionsResult?.limit || limit,
                total: inspectionsResult?.total || 0,
                totalPages: inspectionsResult?.total_pages || 1
            });
            
        } catch (error) {
            addNotification?.({ 
                type: 'danger', 
                message: `Failed loading inspections: ${error.message}` 
            });
            setRecentInspections([]);
            setInspectionPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
        } finally {
            setIsInspectionsLoading(false);
        }
    }, [addNotification, getConfiguredFilters]);

    const handleViewStationDetails = useCallback(async (station) => {
        setDetailsStation(station);
        setShowStationDetailsModal(true);
        setTrendData([]);
        setRecentInspections([]);
        setInspectionPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
        setIsTrendLoading(true);

        // Load first page of inspections for the table
        loadStationInspections(station.station_id, 1, 10);

        // Load trend data separately
        try {
            const configuredFilters = getConfiguredFilters();
            const trendResult = await api.getStationTrend(station.station_id, configuredFilters);
            setTrendData(trendResult || []);
        } catch (error) {
            addNotification?.({ 
                type: 'warning', 
                message: `Could not load trend data: ${error.message}` 
            });
        } finally {
            setIsTrendLoading(false);
        }
    }, [addNotification, loadStationInspections, getConfiguredFilters]);
    
    const handleCloseStationDetails = useCallback(() => {
        setShowStationDetailsModal(false);
        setDetailsStation(null);
        setTrendData([]);
        setRecentInspections([]);
        setInspectionPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
    }, []);

    const handleInspectionPageChange = useCallback((newPage) => {
        if (detailsStation && newPage >= 1 && newPage <= inspectionPagination.totalPages) {
            loadStationInspections(detailsStation.station_id, newPage, inspectionPagination.limit);
        }
    }, [detailsStation, inspectionPagination.totalPages, inspectionPagination.limit, loadStationInspections]);

    // Handlers for the reusable InspectionComponents
    const handleViewInspectionDetails = useCallback((inspection) => {
        setSelectedInspection(inspection);
        setShowInspectionDetails(true);
    }, []);

    const handleCloseInspectionDetails = useCallback(() => {
        setShowInspectionDetails(false);
        setSelectedInspection(null);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {
        setLargeImage(imageUrl);
    }, []);
    
    const handleCloseLargeImage = useCallback(() => {
        setLargeImage(null);
    }, []);

    const handleSetGroundTruth = useCallback(async (inspectionId, status) => {
        setIsGroundTruthLoading(true);
        try {
            const data = await api.confirmClassification(inspectionId, status);
            const updatedInfo = { 
                confirmation_status: status, 
                confirmed_by: data?.username || 'user',
                confirmation_timestamp: new Date().toISOString()
            };

            setRecentInspections(prev => prev.map(insp => 
                insp._id === inspectionId ? { ...insp, ...updatedInfo } : insp
            ));

            if (selectedInspection?._id === inspectionId) {
                setSelectedInspection(prev => ({ ...prev, ...updatedInfo }));
            }
            
            addNotification?.({ type: 'success', message: `Ground truth set as ${status}` });
        } catch (error) {
            addNotification?.({ 
                type: 'danger', 
                message: `Failed to update ground truth: ${error.message}` 
            });
        } finally {
            setIsGroundTruthLoading(false);
        }
    }, [selectedInspection, addNotification]);

    const sortedStations = useMemo(() => {
        if (!stations || stations.length === 0) return [];
        
        return [...stations].sort((a, b) => {
            const statsA = a.statistics || {};
            const statsB = b.statistics || {};
            
            const rateA = statsA.total_inspections > 0 ? (statsA.defect_count / statsA.total_inspections) : 0;
            const rateB = statsB.total_inspections > 0 ? (statsB.defect_count / statsB.total_inspections) : 0;
            return rateB - rateA;
        });
    }, [stations]);

    const handleBarClick = useCallback((stationId) => {
        const station = stations.find(s => s.station_id === stationId);
        if (station) {
            handleViewStationDetails(station);
        }
    }, [stations, handleViewStationDetails]);

    if (!hasInitialized) {
        return <LoadingSpinner message="Initializing filters..." size="large" />;
    }
    
    return (
        <ErrorBoundary error={state.error} onRetry={handleRefresh}>
            <div className="stations-trends-content fade-in">
                <div className="mb-4">
                    <h4 className="mb-1">Station Performance & Trends</h4>
                    <p className="text-muted mb-0">Monitor and analyze the performance trends of individual inspection stations.</p>
                </div>

                <UnifiedFilterPanel 
                    filters={filters} 
                    onFilterChange={handleFilterChange}
                    availableOptions={availableOptions}
                    config={{ includeManualImages, itemsPerPage }}
                    tabType="stations"
                    showConfigInfo={true}
                />

                {isLoading ? (
                    <LoadingSpinner message="Loading station data..." size="large" />
                ) : sortedStations.length > 0 ? (
                    <>
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-chart-bar text-primary me-2"></i>
                                    Stations Comparison
                                </h5>
                                <small className="text-muted">Click on bars to view detailed trends</small>
                            </div>
                            <div className="card-body" style={{ height: '450px' }}>
                                <StationComparisonChart 
                                    chartId="station-comparison" 
                                    data={sortedStations} 
                                    onBarClick={handleBarClick} 
                                />
                            </div>
                        </div>

                        <div className="row">
                            {sortedStations.map(station => (
                                <StationCard 
                                    key={station.station_id} 
                                    station={station}
                                    onViewDetails={handleViewStationDetails}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="card shadow-sm">
                        <div className="card-body text-center p-5">
                            <i className="fas fa-server fa-4x text-muted mb-4"></i>
                            <h4 className="text-muted">No Station Data Found</h4>
                            <p className="text-muted">
                                No stations were found matching the current filters. 
                                Try adjusting your filter criteria or check if stations are properly configured.
                            </p>
                            <button 
                                className="btn btn-primary mt-3" 
                                onClick={resetFilters}
                                title="Reset all filters to defaults"
                            >
                                <i className="fas fa-undo me-2"></i>Reset Filters
                            </button>
                        </div>
                    </div>
                )}
                
                <StationDetailsModal 
                    station={detailsStation} 
                    isOpen={showStationDetailsModal} 
                    onClose={handleCloseStationDetails} 
                    trendData={trendData} 
                    isTrendLoading={isTrendLoading} 
                    recentInspections={recentInspections} 
                    isInspectionsLoading={isInspectionsLoading} 
                    onViewInspectionDetails={handleViewInspectionDetails} 
                    onImageClick={handleImageClick} 
                    onSetGroundTruth={handleSetGroundTruth} 
                    isGroundTruthLoading={isGroundTruthLoading} 
                    addNotification={addNotification}
                    inspectionPagination={inspectionPagination}
                    onInspectionPageChange={handleInspectionPageChange}
                />
                
                {showInspectionDetails && selectedInspection && (
                    <ReusableInspectionDetailsModal 
                        inspection={selectedInspection} 
                        modelList={modelList} 
                        onClose={handleCloseInspectionDetails} 
                        onImageClick={handleImageClick} 
                        onSetGroundTruth={handleSetGroundTruth} 
                        isLoading={isGroundTruthLoading} 
                        addNotification={addNotification} 
                    />
                )}

                <ImageModal 
                    src={largeImage} 
                    alt="Enlarged view" 
                    show={!!largeImage} 
                    onClose={handleCloseLargeImage} 
                />
            </div>
        </ErrorBoundary>
    );
};
